import { get } from 'lodash-es'
import { Scan } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { PageLayout } from '@/components'
import { useAppDispatch, useAppSelector } from '../../../store'
import { login } from '../../ducks'
import { resetLogin } from '../../ducks/login'
import { FeatureCarousel } from '../FeatureCarousel'
import { LoginPasswordForm } from './LoginPasswordForm'
import { LoginPhoneForm } from './LoginPhoneForm'
import { Card, CardContent } from '@/components/ui'
import translate from '@/core/services/translate'
import { getTranslation } from '@/account/services/auth'
import { Icon } from '@/common/components/Icon'

const LoginPage: React.FC = () => {
  const location = useLocation()
  const dispatch = useAppDispatch()
  const language = getTranslation()
  const { error, isLoggingIn } = useAppSelector((state) => state.account.login)

  const [phone, setPhone] = useState<string>('')

  const submitLogin = (password: string) => {
    const redirectedFrom = get(location, 'state.from.pathname') || 'home'
    const redirectTo =
      redirectedFrom !== '/account/logout' ? redirectedFrom : '/stages'
    dispatch(login(phone, password, redirectTo))
  }

  useEffect(() => {
    dispatch(resetLogin())
  }, [resetLogin])

  const features = [
    {
      icon: (
        <Icon
          icon="user-search"
          width="42"
          height="42"
          viewBox="0 0 42 42"
          fill="none"
        />
      ),
      title: 'Collect User Data',
      description:
        'We understand how challenging it can be to target the right audience. Our solution collects necessary information to ensure that your marketing efforts are precisely at your target audience.',
    },
    {
      icon: (
        <Icon
          icon="ticket-customize"
          width="42"
          height="42"
          viewBox="0 0 42 42"
          fill="none"
        />
      ),
      title: 'Extremely customize tickets',
      description:
        'Do you want to provide your customers with the most diverse and valuable benefits? How about offering them the opportunity to purchase combo or ultra VIP tickets? Our solution empowers you to customize tickets exactly to your preferences.',
    },
    {
      icon: (
        <Icon
          icon="users"
          width="42"
          height="42"
          viewBox="0 0 42 42"
          fill="none"
        />
      ),
      title: 'Generate tickets for your business partners',
      description:
        'You can seamlessly track, distribute, and monitor complimentary tickets or invitations with the same level of precision and oversight as paid tickets, preventing any potential mismanagement or loss of control.',
    },
    {
      icon: (
        <Icon
          icon="calendar-search"
          width="42"
          height="42"
          viewBox="0 0 42 42"
          fill="none"
        />
      ),
      title: 'Monthly Payments or Vouchers',
      description:
        "Rhuna's Retargeting Funnel is designed to re-engage potential customers who may have shown interest but not completed desired actions. It helps you bring them back into the conversion funnel.",
    },
  ]

  return (
    <PageLayout authPage>
      <div className="container flex flex-col lg:flex-row gap-12 lg:gap-0 lg:mt-12 max-h-[calc(100vh-200px)] overflow-y-auto py-12">
        {/* Left Side - Features Section */}
        <div className="relative lg:w-1/2 flex flex-col">
          {/* Feature Carousel */}
          <div className="flex-1 flex items-center justify-center">
            <FeatureCarousel features={features} />
          </div>
        </div>

        {/* Right Side - Login Form */}
        <Card className="lg:w-[527px] bg-monochrome-3-60">
          <CardContent className="flex flex-col gap-5 items-center w-full lg:px-24 py-16">
            <div className="w-full">
              <div className="text-title-m text-center mb-2">
                {translate(language.value, 'login.title')}
              </div>
              <div className="text-body-medium text-monochrome-2 text-center mb-8">
                {translate(language.value, 'login.description')}
              </div>

              {!phone ? (
                <LoginPhoneForm
                  submitPhone={(phone) => {
                    setPhone(phone.replace(/\s/g, ''))
                  }}
                />
              ) : (
                <LoginPasswordForm
                  phone={phone}
                  submitError={error}
                  submitPassword={submitLogin}
                  isLoggingIn={isLoggingIn}
                />
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  )
}

export default LoginPage
